import React, { useEffect, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Box, Typography, Button } from '@mui/material';
import { AddCircleOutline as AddRowIcon } from '@mui/icons-material';
import CriteriaRow from './CriteriaRow';
import { NotificationEntity } from '../../services/patientNotificationService';
import { CLINIC_DIALOG_SX } from '../../constants/popupStyles';
import { CriteriaItem } from '../../services/promptService';

interface FormValues {
  items: CriteriaItem[];
}

interface Props {
  label: string;
  emptyMessage: string;
  value: CriteriaItem[];
  onChange: (items: CriteriaItem[]) => void;
  entity: NotificationEntity;
  dynamicOptions: Record<string, { id: number | string; name: string }[]>;
}

const CriteriaEditorPanel: React.FC<Props> = ({ label, emptyMessage, value, onChange, entity, dynamicOptions }) => {
  const { control, setValue, watch } = useForm<FormValues>({
    defaultValues: { items: value },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  const watched = watch('items');
  const serializedRef = useRef<string>(JSON.stringify(value));

  useEffect(() => {
    const serialized = JSON.stringify(watched);
    if (serialized !== serializedRef.current) {
      serializedRef.current = serialized;
      onChange(watched ?? []);
    }
  }, [watched, onChange]);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="body2" sx={{ color: CLINIC_DIALOG_SX.fieldLabel.color, fontWeight: 500 }}>
          {label}
        </Typography>
        <Button
          size="small"
          startIcon={<AddRowIcon />}
          onClick={() => append({ field: '', operator: 'equals', value: '' })}
          sx={{ textTransform: 'none', color: CLINIC_DIALOG_SX.fieldLabel.color }}
        >
          Add Rule
        </Button>
      </Box>
      {fields.map((field, index) => (
        <CriteriaRow
          key={field.id}
          index={index}
          prefix="items"
          onRemove={remove}
          entity={entity}
          control={control}
          setValue={setValue}
          dynamicOptions={dynamicOptions}
        />
      ))}
      {fields.length === 0 && (
        <Typography variant="caption" color="text.secondary">{emptyMessage}</Typography>
      )}
    </Box>
  );
};

export default CriteriaEditorPanel;
